import { DEFAULT_PUBLIC_COLORS, isHexColor, toLongHex } from "../../operator-settings/utils/publicBranding.js"

const LIGHT_TEXT = "#ffffff"
const DARK_TEXT = "#000000"

const COLOR_VARIABLES = {
	background: "--m-background-color",
	card: "--m-card-background-color",
	text: "--m-text",
	text_muted: "--m-text-muted",
	border: "--m-border-color",
}

const toRgb = (hex) => {
	const long = toLongHex(hex)
	return [1, 3, 5].map((i) => parseInt(long.slice(i, i + 2), 16))
}

const toHex = (rgb) => `#${rgb.map((c) => Math.round(c).toString(16).padStart(2, "0")).join("")}`

/**
 * Mixes a color with black or white.
 *
 * @param {string} hex - base color
 * @param {number} amount - 0 to 1, how much of the target to mix in
 * @param {"black"|"white"} target - color to mix towards
 *
 * @returns {string} lowercase #rrggbb
 * */
export const mixColor = (hex, amount, target) => {
	const end = target === "white" ? 255 : 0
	return toHex(toRgb(hex).map((c) => c + (end - c) * amount))
}

const luminance = (hex) => {
	const [r, g, b] = toRgb(hex).map((c) => {
		const s = c / 255
		return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4
	})
	return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

/**
 * WCAG contrast ratio between two colors.
 *
 * @param {string} a - first color
 * @param {string} b - second color
 *
 * @returns {number} ratio from 1 to 21
 * */
export const contrastRatio = (a, b) => {
	const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x)
	return (hi + 0.05) / (lo + 0.05)
}

/**
 * Text color that reads best on top of a background.
 *
 * @param {string} background - background color
 *
 * @returns {string} white or a near-black, whichever has more contrast
 * */
export const readableTextOn = (background) => (contrastRatio(background, LIGHT_TEXT) >= contrastRatio(background, DARK_TEXT) ? LIGHT_TEXT : DARK_TEXT)

const isCustom = (key, value) => isHexColor(value) && toLongHex(value) !== toLongHex(DEFAULT_PUBLIC_COLORS[key])

/**
 * CSS variables for the public pages from the operator branding.
 *
 * Only colors that differ from the EvPOS defaults are emitted, so an unbranded page keeps the stylesheet values
 * (including the dark theme). The primary color also drives its bolder, pressed and subtle variants and the text over it.
 *
 * @param {{ colors?: Object }|null} branding - branding from the public API
 *
 * @returns {Object} style object of CSS variables, empty when nothing is customized
 * */
export const brandingStyle = (branding) => {
	const colors = branding?.colors
	if (!colors) return {}

	const style = {}

	if (isCustom("primary", colors.primary)) {
		const primary = toLongHex(colors.primary)
		style["--m-primary"] = primary
		style["--m-loader-color"] = primary
		style["--m-primary-bolder"] = mixColor(primary, 0.16, "black")
		style["--m-primary-xbolder"] = mixColor(primary, 0.29, "black")
		style["--m-icon-primary"] = primary
		style["--m-background-primary"] = mixColor(primary, 0.06, "black")
		style["--m-background-primary-hovered"] = mixColor(primary, 0.16, "black")
		style["--m-background-primary-pressed"] = mixColor(primary, 0.22, "black")
		style["--m-background-subtle-primary"] = mixColor(primary, 0.92, "white")
		style["--m-background-subtle-primary-hovered"] = mixColor(primary, 0.84, "white")
		style["--m-background-subtle-primary-pressed"] = mixColor(primary, 0.76, "white")
		style["--m-on-primary"] = readableTextOn(style["--m-background-primary"])
	}

	for (const [key, variable] of Object.entries(COLOR_VARIABLES)) {
		if (isCustom(key, colors[key])) style[variable] = toLongHex(colors[key])
	}

	return style
}
